import { useEffect, useRef, useCallback, useState } from 'react';
import { useLivePreviewStore } from '../store/livePreviewStore';
import { PerformanceStats } from '../../core/types/live-preview.types';

interface GameLoopOptions {
  onUpdate?: (deltaTime: number, frame: number) => void;
  onFixedUpdate?: (fixedDeltaTime: number) => void;
}

interface GameLoopState {
  isRunning: boolean;
  fps: number;
  frameTime: number;
  frame: number;
}

const FIXED_TIMESTEP = 1 / 60; // 60 Hz fixed update
const FPS_SAMPLE_SIZE = 60; // Number of frames to average

export function useGameLoop(options: GameLoopOptions = {}) {
  const { playState, playModeSettings, updateStats } = useLivePreviewStore();

  // Store callbacks in refs to avoid re-creating tick on every render
  const onUpdateRef = useRef(options.onUpdate);
  const onFixedUpdateRef = useRef(options.onFixedUpdate);

  // Keep refs up to date
  useEffect(() => {
    onUpdateRef.current = options.onUpdate;
    onFixedUpdateRef.current = options.onFixedUpdate;
  });

  const [state, setState] = useState<GameLoopState>({
    isRunning: false,
    fps: 0,
    frameTime: 0,
    frame: 0,
  });

  // Refs for timing
  const frameRef = useRef<number | null>(null);
  const lastFrameTimeRef = useRef<number>(0);
  const accumulatorRef = useRef<number>(0);
  const frameCountRef = useRef<number>(0);

  // FPS tracking
  const fpsHistoryRef = useRef<number[]>([]);
  const frameTimeHistoryRef = useRef<number[]>([]);
  const lastFpsUpdateRef = useRef<number>(0);

  // Stats tracking
  const minFrameTimeRef = useRef<number>(Infinity);
  const maxFrameTimeRef = useRef<number>(0);

  const calculateTargetFrameTime = useCallback(() => {
    if (playModeSettings.vsyncEnabled) {
      return 0; // Let requestAnimationFrame handle pacing natively
    }
    if (playModeSettings.maxFps === 0) {
      return 0; // Unlimited
    }
    return 1000 / playModeSettings.maxFps;
  }, [playModeSettings.vsyncEnabled, playModeSettings.maxFps]);

  const tick = useCallback(
    (timestamp: number) => {
      const currentState = useLivePreviewStore.getState();

      // Check if we should still be running
      if (currentState.playState !== 'playing') {
        frameRef.current = null;
        return;
      }

      // Initialize on first frame
      if (lastFrameTimeRef.current === 0) {
        lastFrameTimeRef.current = timestamp;
        frameRef.current = requestAnimationFrame(tick);
        return;
      }

      // Calculate delta time
      const deltaTime = timestamp - lastFrameTimeRef.current;
      const targetFrameTime = calculateTargetFrameTime();

      // Frame rate limiting (skip if too fast)
      if (targetFrameTime > 0 && deltaTime < targetFrameTime) {
        frameRef.current = requestAnimationFrame(tick);
        return;
      }

      lastFrameTimeRef.current = timestamp;
      frameCountRef.current += 1;

      // Track frame time stats
      const frameTimeMs = deltaTime;
      frameTimeHistoryRef.current.push(frameTimeMs);
      if (frameTimeHistoryRef.current.length > FPS_SAMPLE_SIZE) {
        frameTimeHistoryRef.current.shift();
      }

      minFrameTimeRef.current = Math.min(minFrameTimeRef.current, frameTimeMs);
      maxFrameTimeRef.current = Math.max(maxFrameTimeRef.current, frameTimeMs);

      // Calculate FPS
      const currentFps = 1000 / deltaTime;
      fpsHistoryRef.current.push(currentFps);
      if (fpsHistoryRef.current.length > FPS_SAMPLE_SIZE) {
        fpsHistoryRef.current.shift();
      }

      // Fixed timestep update (physics, etc.)
      accumulatorRef.current += deltaTime / 1000;
      while (accumulatorRef.current >= FIXED_TIMESTEP) {
        onFixedUpdateRef.current?.(FIXED_TIMESTEP);
        accumulatorRef.current -= FIXED_TIMESTEP;
      }

      // Variable update
      onUpdateRef.current?.(deltaTime / 1000, frameCountRef.current);

      // Update stats periodically (every 100ms)
      if (timestamp - lastFpsUpdateRef.current > 100) {
        lastFpsUpdateRef.current = timestamp;

        const avgFps =
          fpsHistoryRef.current.reduce((a, b) => a + b, 0) /
          fpsHistoryRef.current.length;
        const avgFrameTime =
          frameTimeHistoryRef.current.reduce((a, b) => a + b, 0) /
          frameTimeHistoryRef.current.length;

        // Simulate performance stats (in a real implementation, these would come from PixiJS)
        const simulatedStats: Partial<PerformanceStats> = {
          fps: Math.round(avgFps),
          frameTime: avgFrameTime,
          minFrameTime: minFrameTimeRef.current,
          maxFrameTime: maxFrameTimeRef.current,
          avgFrameTime: avgFrameTime,
          // Simulated rendering stats
          drawCalls: Math.floor(10 + Math.random() * 5),
          triangles: Math.floor(500 + Math.random() * 100),
          activeObjects: Math.floor(20 + Math.random() * 10),
          visibleObjects: Math.floor(15 + Math.random() * 8),
          // Simulated memory stats
          memoryUsed: Math.floor(50 + Math.random() * 10),
          memoryTotal: 256,
          texturesLoaded: Math.floor(5 + Math.random() * 3),
          textureMemory: Math.floor(20 + Math.random() * 5),
          audioPlaying: 0,
          audioLoaded: Math.floor(3 + Math.random() * 2),
        };

        updateStats(simulatedStats);

        setState({
          isRunning: true,
          fps: Math.round(avgFps),
          frameTime: avgFrameTime,
          frame: frameCountRef.current,
        });
      }

      // Schedule next frame
      frameRef.current = requestAnimationFrame(tick);
    },
    [calculateTargetFrameTime, updateStats]
  );

  // Start/stop the game loop based on play state
  useEffect(() => {
    if (playState === 'playing') {
      // Reset timing state
      lastFrameTimeRef.current = 0;
      accumulatorRef.current = 0;
      lastFpsUpdateRef.current = 0;
      minFrameTimeRef.current = Infinity;
      maxFrameTimeRef.current = 0;
      fpsHistoryRef.current = [];
      frameTimeHistoryRef.current = [];

      // Start the loop
      frameRef.current = requestAnimationFrame(tick);

      setState((prev) => ({ ...prev, isRunning: true }));
    } else {
      // Stop the loop
      if (frameRef.current !== null) {
        cancelAnimationFrame(frameRef.current);
        frameRef.current = null;
      }

      setState((prev) => ({ ...prev, isRunning: false }));
    }

    return () => {
      if (frameRef.current !== null) {
        cancelAnimationFrame(frameRef.current);
        frameRef.current = null;
      }
    };
  }, [playState, tick]);

  // Reset frame counter when stopping
  useEffect(() => {
    if (playState === 'stopped') {
      frameCountRef.current = 0;
      setState({
        isRunning: false,
        fps: 0,
        frameTime: 0,
        frame: 0,
      });
    }
  }, [playState]);

  const stepFrame = useCallback(() => {
    if (playState !== 'paused') return;

    frameCountRef.current += 1;
    const deltaTime = 1 / 60; // Assume 60fps for step

    onFixedUpdateRef.current?.(FIXED_TIMESTEP);
    onUpdateRef.current?.(deltaTime, frameCountRef.current);

    setState((prev) => ({
      ...prev,
      frame: frameCountRef.current,
    }));
  }, [playState]);

  return {
    ...state,
    stepFrame,
    frameCount: frameCountRef.current,
  };
}
